/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
// @ts-nocheck
import Button from '@react/react-spectrum/Button';
import Dialog from '@react/react-spectrum/Dialog';
import Close from '@react/react-spectrum/Icon/Close';
import ModalContainer from '@react/react-spectrum/ModalContainer';
import {addToast, Toast} from '@react/react-spectrum/Toast';
import Wait from '@react/react-spectrum/Wait';
import axios from 'axios';
import React, {useEffect, useState} from 'react';
import AnalyticsUtil from '../../../utils/AnalyticsUtil';
import {Utils} from '../../../utils/Globals';
import {store} from '../../client';
import {LIBRARIES_FETCHING, LIBRARIES_FETCH_FAIL, LIBRARIES_FETCH_SUCCESS} from '../../types/index';
import showPostDownload from './postDownload';
import './quickTaskModal.scss';
import SaveToLibraryManager from './saveToLibraryManager';
import getConfig from '../../../config/index';

let QUICKACTION_MODAL_KEY: number | null | undefined;

type Props = {
	removeQuickAction: Function;
	actionId: string;
};

const sendAnalytic = (workflow, eventType, eventSubType, eventSubCategory, errordesc) => {
	const analytics = new AnalyticsUtil(true);
	analytics.ingest.initializeDefaultData();
	const data: dataObject = {
		setEventWorkFlow: workflow,
		setEventType: eventType,
		setEventSubtype: eventSubType,
		setEventSubcategory: eventSubCategory,
		setEventErrorDesc: errordesc,
	};
	analytics.logAnalytics(data);
	return analytics.ingest.sendToIngest();
};

export const QuickActionModal = ({removeQuickAction, actionId}: Props) => {
	const [qaLoaded, quickActionLoaded] = useState(false);

	const showToast = (msg, type, label, goToAsset) => {
		addToast(
			<Toast closable variant={type} actionLabel={label} onAction={goToAsset}>
				{msg}
			</Toast>,
			10000,
		);
	};

	const escapeKeyHandler = (event) => {
		if (event.key === 'Esc' || event.key === 'Escape') {
			removeQuickAction();
		}
	};

	useEffect(() => {
		if (document.body) {
			document.body.addEventListener('keydown', escapeKeyHandler);
		}
		if (document.querySelector('#quick-action-modal')) {
			document.querySelector('#quick-action-modal').addEventListener('TRACK_ANALYTICS_EVENT', (e) => {
				switch (e.detail.eventName) {
					case 'quickAction:assetUploaded':
						sendAnalytic('CCX QA', 'render', 'uploadSuccess', 'uploadScreen', null);
						break;
					case 'project:editorDisplayed':
						sendAnalytic('CCX QA', 'render', null, 'uploadScreen', null);
						break;
					case 'quickAction:downloadPressed':
						sendAnalytic('CCX QA', 'click', 'download', 'downloadScreen', null);
						break;
					default:
						null;
				}
			});
		}
		if (!window.qtHost || !window.qtHost.tasks.get(actionId)) {
			// if this is first time we ever execute an action OR  a particular action (meaning we've executed other actions)
			let qtHostEle = null;
			let tasks = null;
			if (!window.qtHost) {
				// first time ever executing an action
				qtHostEle = document.createElement('div');
				qtHostEle.setAttribute('id', 'qt-host');
				// we use a map to hold the quickaction object and for repeated uses this same obj will be appended
				tasks = new Map();
			}
			else {
				// we've executed other actions, but first time for the current action
				// we reuse the qtHostEle and tasks that was saved in window.qtHost
				qtHostEle = window.qtHost.qtHostEle;
				tasks = window.qtHost.tasks;
			}

			window.qtHost = {
				tasks,
				task: null,
				id: null,
				qtHostEle,
				qtLoaded(QuickTask) {
					// qtLoaded will only be called once when the quick action script is loaded
					this.tasks.set(
						actionId,
						new QuickTask(qtHostEle, {
							navigate(e, data) {
								console.log('navigate', e, this.navigationData);
							},
							navigationData: {
								config: {
									'export-targets': JSON.stringify(['Download']),
								},
							},
							browserInfo: {isMobile: false},
							done() {},
						}),
					);
					this.task = this.tasks.get(actionId);
					const qtHolder = document.querySelector('.quick-action-holder');
					if (qtHolder) {
						qtHolder.appendChild(qtHostEle);
						const taskId = actionId;
						// this event will be triggered when the final image is downloaded
						qtHolder.addEventListener(`${taskId}__downloadable-image`, (event) => {
							const {
								fileData: {base64URL, blob, fileName, size, type},
							} = event.detail;
							removeQuickAction();
							showPostDownload(blob, type, fileName, actionId);
						});
					}
					while (qtHostEle.firstChild) {
						qtHostEle.firstChild.remove();
					}
					this.task.render();
				},
			};
			// fetching the entry file of the quick action
			const fetchURL = window.cchome.util.hasFeature('ccx_sept')
				? window.CONFIG.CCX_QA_FETCH
				: window.CONFIG.SPARK_QA_FETCH;
			axios
				.get(`${fetchURL}/${actionId}`)
				.then((resp) => {
					const script: any = document.createElement('script');
					script.onload = () => {
						quickActionLoaded(true);
					};
					script.src = resp.data[0].entry;
					script.async = true;
					document.body ? document.body.appendChild(script) : '';
				})
				.catch((err) => {
					sendAnalytic('CCX QA', 'api-error', 'error', null, JSON.stringify(err));
					showToast(Utils.localizedStrings['ccx.quickTask.toastFail'], 'error', '', () => {});
					removeQuickAction();
				});
		}
		else {
			// repeated actions
			const qtHolder = document.querySelector('.quick-action-holder');
			// we append the original qtHoseEle into the new container
			if (qtHolder) {
				qtHolder.appendChild(window.qtHost.qtHostEle);
				const taskId = actionId;
				// this event will be triggered when the final image is downloaded
				qtHolder.addEventListener(`${taskId}__downloadable-image`, (event) => {
					const {
						fileData: {base64URL, blob, fileName, size, type},
					} = event.detail;
					removeQuickAction();
					showPostDownload(blob, type, fileName, actionId);
				});
			}
			// we set the current task to be the one we save in map
			window.qtHost.task = window.qtHost.tasks.get(actionId);
			quickActionLoaded(true);
			while (window.qtHost.qtHostEle.firstChild) {
				window.qtHost.qtHostEle.firstChild.remove();
			}
			window.qtHost.task.render();
		}
		return () => {
			if (document.body) {
				document.body.removeEventListener('keydown', escapeKeyHandler);
			}
		};
	}, []);

	return (
		<Dialog
			autoFocusButton={null}
			backdropClickable={false}
			confirmDisabled={false}
			disableEscKey
			isDismissible={false}
			keyboardConfirm={false}
			id='quick-action-modal'
			className='quick-action-modal'
			openUrl
			role='dialog'
		>
			<div className='quick-action-holder'>
				<div className={qaLoaded ? 'quick-action-loader-close' : 'quick-action-loader-open'}>
					<Wait />
				</div>
			</div>
			<Button
				variant='tool'
				icon={<Close />}
				aria-label='Close'
				onClick={removeQuickAction}
				id='quick-task-close-button'
			/>
		</Dialog>
	);
};

/**
 * @param {string} actionId - SAP Code for the app
 */
export default function addQuickTaskModal(actionId: string): void {
	if (QUICKACTION_MODAL_KEY || !actionId) {
		return;
	}

	if (window.cchome.util.hasFeature('ccx_cards')) {
		store.dispatch({
			type: LIBRARIES_FETCHING,
		});
		SaveToLibraryManager
			.fetchLibraries()
			.then((resp) => {
				store.dispatch({
					type: LIBRARIES_FETCH_SUCCESS,
					payload: resp,
				});
			})
			.catch((err) => {
				// failed to fetch user libraries
				store.dispatch({
					type: LIBRARIES_FETCH_FAIL,
					payload: true,
				});
			});
	}

	const handleOnClose = () => {
		sendAnalytic('CCX QA', 'click', 'dismiss', null, null);
		ModalContainer.hide(QUICKACTION_MODAL_KEY);
		QUICKACTION_MODAL_KEY = null;
	};

	QUICKACTION_MODAL_KEY = ModalContainer.show(
		<QuickActionModal removeQuickAction={handleOnClose} actionId={actionId} />,
	);
	const accessToken = `Bearer ${window.adobeIMS.getAccessToken()}`;
	const header = {
		Authorization: accessToken,
		'x-api-key': 'CCHomeWeb1',
	};
	axios.get(getConfig('MIMETYPE_TO_EXT_SERVICE'), {headers: header}).then((resp) => {
		Utils.mimeTypeToExt = resp.data.translations;
	});
}
